import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Loader } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

function AIAgentPage() {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: '👋 Привіт! Я Інна, ваш AI-асистент для управління складом CleanPoint.\n\nЯ можу допомогти вам з:\n• 🔴 Критичними товарами (що терміново купити)\n• 🟡 Товарами під загрозою\n• ⚫ Товарами без руху (deadstock)\n• 📋 Рекомендаціями по закупкам\n• 📊 Аналізом продажів\n• 🎯 Оптимізацією складу\n\nЗапитайте мене щось!'
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage
    };

    // Додаємо повідомлення користувача
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Використовуємо змінну оточення або поточний хост
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || `${window.location.protocol}//${window.location.hostname}:8000`;
      
      const response = await fetch(`${apiBaseUrl}/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          messages: [...messages, userMessage]
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Додаємо відповідь асистента
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response
      }]);

    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '❌ Вибачте, сталася помилка. Перевірте підключення до сервера та ваш OpenAI API ключ.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const quickQuestions = [
    "Де в нас найбільший провал в закупках?",
    "Що треба купити на найближчі 2 тижні?",
    "Як оптимізувати склад?",
    "Які товари без руху?",
    "Покажи критичні товари"
  ];

  return (
    <div className="p-4 h-[calc(100vh-4rem)] flex flex-col">
      <div className="mb-3">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Bot className="w-5 h-5 text-blue-600" />
          ШІ Агент - Інна
        </h2>
        <p className="text-xs text-gray-600">
          Асистент для управління складом • Відповідає на основі даних з бази
        </p>
      </div>

      {/* Quick Questions */}
      <div className="mb-3">
        <p className="text-xs text-gray-600 mb-2">Швидкі запити:</p>
        <div className="flex gap-2 flex-wrap">
          {quickQuestions.map((question, idx) => (
            <button
              key={idx}
              onClick={() => setInputMessage(question)}
              className="text-xs px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded border border-gray-300"
              disabled={isLoading}
            >
              {question}
            </button>
          ))}
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto border border-gray-300 rounded bg-white p-4 mb-3">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`mb-4 flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`flex gap-2 max-w-[80%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                msg.role === 'user' ? 'bg-blue-500' : 'bg-gray-200'
              }`}>
                {msg.role === 'user' ? (
                  <User className="w-5 h-5 text-white" />
                ) : (
                  <Bot className="w-5 h-5 text-gray-700" />
                )}
              </div>
              <div className={`px-4 py-2 rounded-lg text-sm ${
                msg.role === 'user'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {msg.role === 'user' ? (
                  <pre className="whitespace-pre-wrap font-sans">{msg.content}</pre>
                ) : (
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    className="prose prose-sm max-w-none markdown-content"
                  >
                    {msg.content}
                  </ReactMarkdown>
                )}
              </div>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start mb-4">
            <div className="flex gap-2">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                <Bot className="w-5 h-5 text-gray-700" />
              </div>
              <div className="px-4 py-2 rounded-lg bg-gray-100">
                <Loader className="w-5 h-5 animate-spin text-gray-600" />
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="flex gap-2">
        <textarea
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Запитайте про склад, прогнози, залишки..."
          className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm resize-none"
          rows="2"
          disabled={isLoading}
        />
        <button
          onClick={sendMessage}
          disabled={!inputMessage.trim() || isLoading}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isLoading ? (
            <Loader className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </button>
      </div>

      <p className="text-xs text-gray-500 mt-2 text-center">
        💡 Інна використовує тільки дані з вашої бази даних. Відповіді базуються на реальних цифрах.
      </p>

      <style>{`
        .markdown-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 10px 0;
          font-size: 12px;
        }
        .markdown-content th,
        .markdown-content td {
          border: 1px solid #ddd;
          padding: 6px 8px;
          text-align: left;
        }
        .markdown-content th {
          background-color: #f3f4f6;
          font-weight: 600;
        }
        .markdown-content tr:nth-child(even) {
          background-color: #f9fafb;
        }
        .markdown-content h1,
        .markdown-content h2,
        .markdown-content h3 {
          margin-top: 12px;
          margin-bottom: 8px;
        }
        .markdown-content p {
          margin: 8px 0;
        }
        .markdown-content ul,
        .markdown-content ol {
          margin: 8px 0;
          padding-left: 20px;
        }
      `}</style>
    </div>
  );
}

export default AIAgentPage;

